import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Unterschriftenfeld.
 *
 * Auf einem Handy ist das die Stelle, an der die meisten Web-Apps scheitern,
 * und immer an denselben drei Dingen:
 *
 * 1. `touch-action: none`. Ohne diese eine CSS-Zeile scrollt der Browser die
 *    Seite, während der Kunde unterschreibt. Man bekommt einen Strich von drei
 *    Millimetern und eine verschobene Seite. Die Zeile steht in styles.css und
 *    zusätzlich hier als Inline-Stil, damit sie niemand versehentlich
 *    wegkaskadiert.
 *
 * 2. Pointer Events statt Touch- und Maus-Ereignissen nebeneinander. Sonst
 *    kommt jeder Strich doppelt an, und auf Geräten mit Stift gar nicht.
 *    `setPointerCapture` sorgt dafür, dass ein Strich weitergeht, auch wenn
 *    der Finger kurz über den Rand rutscht.
 *
 * 3. Die Auflösung. Ein Canvas mit CSS-Breite 320 und Attributbreite 320 ist
 *    auf einem Retina-Display unscharf; die Unterschrift sieht aus wie mit
 *    einem Filzstift gemalt. Gezeichnet wird deshalb in Gerätepixeln.
 *
 * Ausgegeben wird ein beschnittenes PNG: nur der Bereich, in dem tatsächlich
 * etwas steht, plus ein kleiner Rand. Ein 1600 × 400 grosses Bild mit einem
 * Kringel in der Ecke ist auf einer Rechnung unbrauchbar.
 */

export interface SignaturePadProps {
  onChange?: (hasInk: boolean) => void
  height?: number
  label?: string
  id?: string
}

export interface SignaturePadHandle {
  isEmpty: () => boolean
  toPngDataUrl: () => string | null
  clear: () => void
}

interface Punkt {
  x: number
  y: number
}

export function useSignaturePad(): {
  props: { padRef: React.MutableRefObject<SignaturePadHandle | null> }
  handle: React.MutableRefObject<SignaturePadHandle | null>
} {
  const handle = useRef<SignaturePadHandle | null>(null)
  return { props: { padRef: handle }, handle }
}

export function SignaturePad({
  padRef,
  onChange,
  height = 200,
  label = 'Hier unterschreiben',
  id = 'signature-pad'
}: SignaturePadProps & {
  padRef: React.MutableRefObject<SignaturePadHandle | null>
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const zeichnet = useRef(false)
  const letzter = useRef<Punkt | null>(null)
  /** Umschliessendes Rechteck des Geschriebenen, in Gerätepixeln. */
  const box = useRef<{ minX: number; minY: number; maxX: number; maxY: number } | null>(null)
  const [hasInk, setHasInk] = useState(false)

  const dpr = typeof window === 'undefined' ? 1 : Math.min(window.devicePixelRatio || 1, 3)

  const setup = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const breite = canvas.clientWidth
    canvas.width = Math.round(breite * dpr)
    canvas.height = Math.round(height * dpr)
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.lineWidth = 2.2 * dpr
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    // Schwarz auf Weiss, unabhängig vom Farbschema: die Unterschrift landet
    // auf einem gedruckten Beleg, nicht auf dem Bildschirm.
    ctx.strokeStyle = '#111111'
  }, [dpr, height])

  const letzteBreite = useRef(0)

  useEffect(() => {
    setup()
    letzteBreite.current = canvasRef.current?.clientWidth ?? 0

    /**
     * Nur bei echter Breitenänderung neu aufsetzen.
     *
     * Das klang zuerst nach Kleinigkeit und ist der wichtigste Punkt an
     * diesem Feld: auf einem Handy feuert resize auch, wenn die Tastatur
     * auf- oder zugeht – und das passiert direkt bevor jemand unterschreibt,
     * nämlich nach dem Eintippen des Namens. Wer bei jedem resize leert,
     * löscht dem Kunden die Unterschrift unter der Hand weg.
     *
     * Die Breite ändert sich nur beim Drehen des Geräts. Dann ist das
     * Zurücksetzen richtig, weil ein verzerrter Strich schlimmer wäre.
     */
    const onResize = (): void => {
      const breite = canvasRef.current?.clientWidth ?? 0
      if (breite === letzteBreite.current) return
      letzteBreite.current = breite
      setup()
      box.current = null
      setHasInk(false)
      onChange?.(false)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [setup, onChange])

  const punktAus = (e: React.PointerEvent<HTMLCanvasElement>): Punkt => {
    const canvas = canvasRef.current as HTMLCanvasElement
    const rect = canvas.getBoundingClientRect()
    return {
      x: (e.clientX - rect.left) * dpr,
      y: (e.clientY - rect.top) * dpr
    }
  }

  const merkeBox = (p: Punkt): void => {
    const b = box.current
    box.current = b
      ? {
          minX: Math.min(b.minX, p.x),
          minY: Math.min(b.minY, p.y),
          maxX: Math.max(b.maxX, p.x),
          maxY: Math.max(b.maxY, p.y)
        }
      : { minX: p.x, minY: p.y, maxX: p.x, maxY: p.y }
  }

  const start = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    // Der Strich gehört ab jetzt diesem Zeiger – auch ausserhalb des Felds.
    canvas.setPointerCapture(e.pointerId)
    zeichnet.current = true
    const p = punktAus(e)
    letzter.current = p
    merkeBox(p)

    // Ein Tippen ohne Bewegung soll einen Punkt hinterlassen, keinen Nichts.
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.beginPath()
      ctx.arc(p.x, p.y, ctx.lineWidth / 2, 0, Math.PI * 2)
      ctx.fillStyle = '#111111'
      ctx.fill()
    }
    if (!hasInk) {
      setHasInk(true)
      onChange?.(true)
    }
  }

  const bewege = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!zeichnet.current) return
    e.preventDefault()
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx || !letzter.current) return

    // getCoalescedEvents liefert die Zwischenpunkte, die der Browser
    // zusammengefasst hat. Ohne sie wird eine schnelle Unterschrift eckig.
    const punkte =
      typeof e.nativeEvent.getCoalescedEvents === 'function'
        ? e.nativeEvent.getCoalescedEvents()
        : [e.nativeEvent]

    ctx.beginPath()
    ctx.moveTo(letzter.current.x, letzter.current.y)
    const rect = canvas.getBoundingClientRect()
    for (const roh of punkte) {
      const p = { x: (roh.clientX - rect.left) * dpr, y: (roh.clientY - rect.top) * dpr }
      ctx.lineTo(p.x, p.y)
      merkeBox(p)
      letzter.current = p
    }
    ctx.stroke()
  }

  const ende = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!zeichnet.current) return
    zeichnet.current = false
    letzter.current = null
    canvasRef.current?.releasePointerCapture(e.pointerId)
  }

  const leeren = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    box.current = null
    setHasInk(false)
    onChange?.(false)
  }, [onChange])

  /**
   * Beschnittenes PNG.
   *
   * Beschnitten auf das, was tatsächlich geschrieben wurde, mit etwas Rand.
   * Der weisse Untergrund wird bewusst gezeichnet: ein PNG mit durchsichtigem
   * Hintergrund wird auf einem dunklen Beleg unsichtbar.
   */
  const toPng = useCallback((): string | null => {
    const canvas = canvasRef.current
    const b = box.current
    if (!canvas || !b) return null

    const rand = Math.round(8 * dpr)
    const x = Math.max(0, Math.floor(b.minX - rand))
    const y = Math.max(0, Math.floor(b.minY - rand))
    const w = Math.min(canvas.width - x, Math.ceil(b.maxX - b.minX + rand * 2))
    const h = Math.min(canvas.height - y, Math.ceil(b.maxY - b.minY + rand * 2))
    if (w <= 0 || h <= 0) return null

    const ziel = document.createElement('canvas')
    ziel.width = w
    ziel.height = h
    const ctx = ziel.getContext('2d')
    if (!ctx) return null
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, w, h)
    ctx.drawImage(canvas, x, y, w, h, 0, 0, w, h)
    return ziel.toDataURL('image/png')
  }, [dpr])

  useEffect(() => {
    padRef.current = {
      isEmpty: () => box.current === null,
      toPngDataUrl: toPng,
      clear: leeren
    }
  }, [padRef, toPng, leeren])

  return (
    <div className="sigpad">
      <canvas
        id={id}
        ref={canvasRef}
        className="sigpad-canvas"
        // Die eine Zeile, ohne die das Feld auf dem Handy unbrauchbar ist.
        style={{ touchAction: 'none', height: `${height}px` }}
        onPointerDown={start}
        onPointerMove={bewege}
        onPointerUp={ende}
        onPointerCancel={ende}
        onPointerLeave={ende}
        aria-label={label}
        role="img"
      />
      <div className="sigpad-foot">
        <span className="muted small">{hasInk ? 'Unterschrift erfasst' : label}</span>
        <button type="button" className="sigpad-clear" onClick={leeren} disabled={!hasInk}>
          Nochmal
        </button>
      </div>
    </div>
  )
}
